// How long the free trial is, as the site says it.
//
// The length is a dial in the console (`platform_plans.trial_days`), so the site
// may not write it down: a sentence that named the number went false the day the
// operator turned the dial. It is said in two steps instead.
//
//   AT BUILD the number is baked into the document as plain text - a crawler and
//   a reader with no script get a complete sentence, and nothing moves on load.
//   The value baked is TRIAL_DAYS_BAKED, the length a workspace is born on unless
//   the operator has said otherwise.
//
//   ON LOAD the page asks the API what the dial says (`GET /trial-offer`) and, only
//   if it differs from what was baked, re-says the sentences that state it.
//
// This site has no i18n runtime, and this does not add one: every word the page
// can show is still in the document. For each sentence that states the trial the
// build writes out the form for each plural category the language uses ("1 day",
// "2 days"; "1 день", "2 дні", "5 днів") with the number left as `{n}`, and the
// page only picks one with the browser's own `Intl.PluralRules` and drops the
// number in. What arrives from the network is a number, held to its bounds, and
// is written as TEXT - never as markup.

/**
 * The length baked into the document. It mirrors `org.DefaultTrialDays`, and a Go
 * test reads this line to hold the two equal (`org/plan_trial_test.go`).
 */
export const TRIAL_DAYS_BAKED = 30;

/** The bounds `GET /trial-offer` promises, held here too: the network is not trusted to keep them. */
export const TRIAL_DAYS_MIN = 1;
export const TRIAL_DAYS_MAX = 365;

/** The ICU argument a catalog value states the trial with. */
const TRIAL_ARG = 'days';

/** Formats one ICU message. The build passes the real formatter in, so the page never bundles it. */
export type IcuFormat = (message: string, locale: string, values: Record<string, number>) => string;

/** Whether a catalog value states the trial's length. */
export function statesTrial(raw: string): boolean {
  return new RegExp(`\\{\\s*${TRIAL_ARG}\\s*,`).test(raw);
}

export function isTrialDays(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= TRIAL_DAYS_MIN && value <= TRIAL_DAYS_MAX;
}

/**
 * One whole number of days for each plural category the language puts whole days
 * in. Three digits where the category has one, so the number is easy to find again
 * in the sentence it was formatted into.
 */
function representatives(locale: string): Map<string, number> {
  const rules = new Intl.PluralRules(locale);
  const found = new Map<string, number>();
  for (let n = TRIAL_DAYS_MIN; n <= TRIAL_DAYS_MAX; n += 1) {
    const category = rules.select(n);
    const held = found.get(category);
    if (held === undefined || (held < 100 && n >= 100)) found.set(category, n);
  }
  return found;
}

/**
 * The sentence in every form the page may need, the number left as `{n}`.
 * Throws rather than guess: a sentence that carries a second number beside the
 * trial's cannot be re-said safely, and that is a thing to learn at build time.
 */
export function trialForms(raw: string, locale: string, format: IcuFormat): Record<string, string> {
  const forms: Record<string, string> = {};
  for (const [category, n] of representatives(locale)) {
    const said = format(raw, locale, { [TRIAL_ARG]: n });
    const number = new RegExp(`(?<!\\d)${n}(?!\\d)`, 'g');
    if ((said.match(number) ?? []).length !== 1) {
      throw new Error(`a sentence that states the trial must carry its number exactly once (${locale}, ${category}): ${said}`);
    }
    forms[category] = said.replace(number, '{n}');
  }
  return forms;
}

const escapeAttr = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Marks every element whose copy states the trial, BEFORE the locale step takes
 * the `data-i18n` markers away: `data-trial` holds the forms, and `data-trial-attr`
 * names the attribute when the sentence lives in one (a meta description).
 * Reports whether the document states the trial at all.
 */
export function markTrial(
  html: string,
  locale: string,
  lookup: (key: string) => string | undefined,
  format: IcuFormat,
): { html: string; states: boolean } {
  let states = false;
  // Attribute values are double-quoted throughout these documents (as the locale
  // step itself relies on), which is what lets a `>` inside one be told from the
  // end of the tag.
  const out = html.replace(/<([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"]|"[^"]*")*)>/g, (tag, name: string, attrs: string) => {
    if (!attrs.includes('data-i18n')) return tag;
    const stating = [...attrs.matchAll(/\sdata-i18n(?:-([a-z-]+))?="([^"]+)"/g)].filter(([, , key]) => {
      const raw = lookup(key!);
      return raw !== undefined && statesTrial(raw);
    });
    if (stating.length === 0) return tag;
    if (stating.length > 1) throw new Error(`<${name}> states the trial twice; the page can re-say one sentence per element`);
    const [, target, key] = stating[0]!;
    if (target === 'html') throw new Error(`${key} states the trial inside markup; the page re-says it as text only`);
    states = true;
    const forms = escapeAttr(JSON.stringify(trialForms(lookup(key!)!, locale, format)));
    const voidTag = /\/\s*$/.test(attrs);
    const kept = attrs.replace(/\s*\/\s*$/, '');
    const where = target === undefined ? '' : ` data-trial-attr="${target}"`;
    return `<${name}${kept} data-trial="${forms}"${where}${voidTag ? ' /' : ''}>`;
  });
  return { html: out, states };
}

/** The form for this many days, in this language; undefined when the forms cannot say it. */
export function sayTrial(forms: Record<string, string>, locale: string, days: number): string | undefined {
  let category = 'other';
  try {
    category = new Intl.PluralRules(locale).select(days);
  } catch {
    // A language tag the browser does not know falls to `other`, which every form set carries.
  }
  const form = forms[category] ?? forms['other'];
  return form === undefined ? undefined : form.split('{n}').join(String(days));
}

/** Re-says every sentence in the document that states the trial. */
export function applyTrial(doc: Document, days: number): void {
  if (!isTrialDays(days)) return;
  const locale = doc.documentElement.lang || 'en';
  for (const el of doc.querySelectorAll<HTMLElement>('[data-trial]')) {
    let forms: Record<string, string>;
    try {
      forms = JSON.parse(el.dataset.trial ?? '') as Record<string, string>;
    } catch {
      continue;
    }
    const said = sayTrial(forms, locale, days);
    if (said === undefined) continue;
    const attr = el.dataset.trialAttr;
    if (attr) el.setAttribute(attr, said);
    else el.textContent = said;
  }
  doc.documentElement.dataset.trialDays = String(days);
}

/**
 * Asks what the dial says and follows it. Silent on every failure: the document
 * already says a complete sentence, and a site that cannot reach the API is still
 * a site.
 */
export async function followTrial(doc: Document = document, fetcher: typeof fetch = fetch): Promise<void> {
  if (!doc.querySelector('[data-trial]')) return;
  try {
    const res = await fetcher('/api/v1/trial-offer', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const body = (await res.json()) as { days?: unknown };
    if (!isTrialDays(body.days)) return;
    if (String(body.days) === doc.documentElement.dataset.trialDays) return;
    applyTrial(doc, body.days);
  } catch {
    // Offline, blocked, or not JSON: what was baked stands.
  }
}
